'use client';

import { useEffect } from 'react';
import { StatisticsList, SubscriptionStatsDetailCard } from '../subscription/cards/StatisticsList';
import { SubscriptionInfo } from './has-subscription/SubscriptionInfo';
import { useHandleNotAuthOrder } from '../subscription/utils/useHandleNotAuthOrder';


// Interface
// *****************************************************
interface PremiumHeroProps {
  locale?: Locale,
  packagesFromServer?: ApiSchema<'ActivationServices'>,
  subscriptionStats?: ApiSchema<'PremiumSubscription'>,
}


/**
 * @description Hero section for Premium page. It can be used for: user has an active subscription,
 * user not subscribed, subscription expired or canceled, user not logged in.
 *
 * * **locale** - the current page locale.
 * * **packagesFromServer** - subscription packages data from the server side request.
 * * **subscriptionStats** - subscription statistics data from the server side request.
 */
export const PremiumHero = (props: PremiumHeroProps) => {
  // Destructure props
  const {
    locale,
    packagesFromServer,
    subscriptionStats,
  } = props;

  // Simplify variables for convenience
  // *****************************************************
  const stats = subscriptionStats?.stats;


  // List of active subscription statistics for cards
  // *****************************************************
  const statsList: SubscriptionStatsDetailCard[] = [
    { // Included jobs
      title: 'premium.stats.included-jobs.label',
      statsCategory: stats?.includedActivationStats,
      shouldRender: true,
      hideUsedColumn: false,
      hideConsumptionMeter: true,
      type: 'job',
    },
    { // Included contacts
      title: 'premium.stats.included-contacts.label',
      statsCategory: stats?.includedContactStats,
      shouldRender: true,
      hideUsedColumn: false,
      type: 'contact',
    },
    { // Extra contacts
      title: 'premium.stats.extra-contacts.label',
      statsCategory: stats?.extraContactStats,
      shouldRender: stats?.extraContactStats?.total !== undefined && stats?.extraContactStats?.total > 0,
      hideUsedColumn: false,
      type: 'contact',
    },
  ];

  // List of cv view statistics for cards
  // *****************************************************
  const statsView: SubscriptionStatsDetailCard[] = [
    { // Included jobs
      title: 'premium.stats.included-jobs.label',
      statsCategory: stats?.includedActivationStats,
      // false if the user has a legacy package with pre-consumption
      shouldRender: !subscriptionStats?.currentPackage?.withPreConsumption,
      hideUsedColumn: false,
      hideTotalColumn: true,
      hideConsumptionMeter: true,
      type: 'job',
    },
    {
      title: 'premium.stats.cv-view.label',
      statsCategory: stats?.viewStats,
      shouldRender: stats?.viewStats?.total !== undefined && stats?.viewStats?.total > 0,
      hideUsedColumn: true,
      type: 'cv',
    },
  ];

  // Handle trigger the upgrade payment modal for logged-in users if there's a subscription order
  // saved in session storage.
  // *****************************************************
  const { isLoggedIn, tryToTriggerUpgrade } = useHandleNotAuthOrder(locale, packagesFromServer);

  // Trigger payment modal on mount if the user is logged-in
  useEffect(() => {
    if (isLoggedIn) tryToTriggerUpgrade();
  }, [isLoggedIn, tryToTriggerUpgrade]);

  // Render component
  // *****************************************************
  return (
    <>
      {/* If the user has an active subscription */}
      {
        subscriptionStats?.currentPackage && (
          <div className="container">

            {/* Subscription info */}
            <SubscriptionInfo
              subscriptionStats={subscriptionStats}
            />

            {/* Legacy package details */}
            {subscriptionStats?.currentPackage.withPreConsumption && (
              <StatisticsList stats={statsList} />
            )}

            {/* New package details */}
            <StatisticsList
              stats={statsView}
              showConsumptionMeter={false}
            />

          </div>
        )
      }
    </>
  );
};
